import type {
  ControlSignalResult,
  LocalTaskInterruptErrorCode,
  LocalTaskInterruptSender,
} from "./control-plane-types.js";
import { resolveUnixSignalForInterruptMode } from "./control-plane-types.js";
import type { InflightTaskControlRegistry } from "./inflight-task-control-registry.js";

type KillProcess = typeof process.kill;

export function createLocalTaskInterruptSender(input: {
  killProcess?: KillProcess;
  registry: InflightTaskControlRegistry;
}): LocalTaskInterruptSender {
  const killProcess = input.killProcess ?? process.kill;

  return (signal): Promise<ControlSignalResult> => {
    const record = input.registry.getTask(signal.taskId);
    if (!record) {
      throw createLocalTaskInterruptError(
        "task.not_found",
        `task '${signal.taskId}' is not inflight`
      );
    }

    const processId = record.processId;
    if (
      typeof processId !== "number" ||
      !Number.isInteger(processId) ||
      processId <= 0
    ) {
      throw createLocalTaskInterruptError(
        "task.not_running",
        `task '${signal.taskId}' does not have an active runtime process`
      );
    }
    const activeProcessId = processId;

    if (!isProcessAlive(killProcess, activeProcessId)) {
      input.registry.updateTaskProcess({
        processId: null,
        taskId: signal.taskId,
      });
      throw createLocalTaskInterruptError(
        "task.not_running",
        `task '${signal.taskId}' runtime process is no longer running`
      );
    }

    const unixSignal = resolveUnixSignalForInterruptMode(signal.mode);
    try {
      killProcess(activeProcessId, unixSignal);
    } catch (error) {
      if (isNoSuchProcessError(error)) {
        input.registry.updateTaskProcess({
          processId: null,
          taskId: signal.taskId,
        });
        throw createLocalTaskInterruptError(
          "task.not_running",
          `task '${signal.taskId}' runtime process is no longer running`
        );
      }
      throw createLocalTaskInterruptError(
        "signal.send_failed",
        error instanceof Error ? error.message : "failed to send local signal"
      );
    }

    return Promise.resolve({
      dispatchId: record.dispatchId,
      processId: activeProcessId,
      taskId: signal.taskId,
      unixSignal,
    });
  };
}

function createLocalTaskInterruptError(
  code: LocalTaskInterruptErrorCode,
  message: string
): Error {
  return Object.assign(new Error(message), {
    code,
  });
}

function isProcessAlive(killProcess: KillProcess, processId: number): boolean {
  try {
    killProcess(processId, 0);
    return true;
  } catch (error) {
    return !isNoSuchProcessError(error);
  }
}

function isNoSuchProcessError(error: unknown): boolean {
  return (
    error instanceof Error && (error as NodeJS.ErrnoException).code === "ESRCH"
  );
}
